import React, { useEffect, useState } from "react";
import { api, money, fmtDateTime, fmtDate } from "@/lib/api";
import { PageHeader, StatusPill } from "@/components/shared";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function Reports() {
  const [dash, setDash] = useState(null);
  const [outstanding, setOutstanding] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [tollBalances, setTollBalances] = useState([]);
  const [reservations, setReservations] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard"),
      api.get("/reports/outstanding"),
      api.get("/reports/top-customers"),
      api.get("/reports/toll-balances"),
      api.get("/reservations"),
    ]).then(([d, o, t, tb, r]) => {
      setDash(d.data);
      setOutstanding(o.data);
      setTopCustomers(t.data);
      setTollBalances(tb.data);
      setReservations(r.data);
    });
  }, []);

  if (!dash) return <div className="p-6 text-sm text-gray-500">Loading…</div>;

  const overdueRows = reservations.filter((r) => r.status === "Overdue");

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <PageHeader title="Reports" subtitle="Business intelligence" testId="reports-page-header" />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="kpi-card">
          <div className="overline">Monthly Revenue</div>
          <div className="font-heading text-2xl font-bold mt-1 tabular-nums text-green-700">{money(dash.monthly_revenue)}</div>
        </div>
        <div className="kpi-card">
          <div className="overline">Fleet Utilization</div>
          <div className="font-heading text-2xl font-bold mt-1 tabular-nums">{dash.fleet_utilization}%</div>
        </div>
        <div className="kpi-card">
          <div className="overline">Weekly Outstanding</div>
          <div className="font-heading text-2xl font-bold mt-1 tabular-nums text-amber-700">{money(outstanding.reduce((s, r) => s + (r.balance_due || 0), 0))}</div>
        </div>
        <div className="kpi-card">
          <div className="overline">Open Tolls Total</div>
          <div className="font-heading text-2xl font-bold mt-1 tabular-nums text-red-700">{money(tollBalances.reduce((s, r) => s + (r.amount || 0), 0))}</div>
        </div>
      </div>

      <Tabs defaultValue="outstanding" className="bg-white border border-gray-200 rounded-lg">
        <TabsList className="m-3 bg-gray-100">
          <TabsTrigger value="outstanding" data-testid="report-tab-outstanding">Outstanding</TabsTrigger>
          <TabsTrigger value="overdue" data-testid="report-tab-overdue">Overdue</TabsTrigger>
          <TabsTrigger value="top" data-testid="report-tab-top">Top Customers</TabsTrigger>
          <TabsTrigger value="tolls" data-testid="report-tab-tolls">Toll Balances</TabsTrigger>
        </TabsList>

        <TabsContent value="outstanding" className="p-3 pt-0">
          {outstanding.length === 0 ? <Empty /> : (
            <Table head={["Customer", "Phone", "Status", "Pickup", "Balance"]}>
              {outstanding.map((r) => (
                <tr key={r.reservation_id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium">{r.customer_name}</td>
                  <td className="px-4 py-2 font-mono text-xs">{r.phone}</td>
                  <td className="px-4 py-2"><StatusPill status={r.status} /></td>
                  <td className="px-4 py-2">{fmtDateTime(r.pickup_datetime)}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-semibold text-amber-700">{money(r.balance_due)}</td>
                </tr>
              ))}
            </Table>
          )}
        </TabsContent>

        <TabsContent value="overdue" className="p-3 pt-0">
          {overdueRows.length === 0 ? <Empty /> : (
            <Table head={["Customer", "Vehicle", "Was Due", "Balance"]}>
              {overdueRows.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium">{r.customer_name}</td>
                  <td className="px-4 py-2">{r.vehicle_label}</td>
                  <td className="px-4 py-2 text-red-700">{fmtDateTime(r.return_datetime)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{money(r.balance_due)}</td>
                </tr>
              ))}
            </Table>
          )}
        </TabsContent>

        <TabsContent value="top" className="p-3 pt-0">
          {topCustomers.length === 0 ? <Empty /> : (
            <Table head={["Customer", "Phone", "Payments", "Total Spent"]}>
              {topCustomers.map((c) => (
                <tr key={c.customer_id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium">{c.customer_name}</td>
                  <td className="px-4 py-2 font-mono text-xs">{c.phone}</td>
                  <td className="px-4 py-2 tabular-nums">{c.payment_count}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-semibold text-green-700">{money(c.total_spent)}</td>
                </tr>
              ))}
            </Table>
          )}
        </TabsContent>

        <TabsContent value="tolls" className="p-3 pt-0">
          {tollBalances.length === 0 ? <Empty /> : (
            <Table head={["Customer", "Plate", "Date", "Status", "Amount"]}>
              {tollBalances.map((t) => (
                <tr key={t.toll_id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium">{t.customer_name}</td>
                  <td className="px-4 py-2 font-mono text-xs">{t.plate}</td>
                  <td className="px-4 py-2">{fmtDate(t.violation_date)}</td>
                  <td className="px-4 py-2"><StatusPill status={t.status} /></td>
                  <td className="px-4 py-2 text-right tabular-nums">{money(t.amount)}</td>
                </tr>
              ))}
            </Table>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Table({ head, children }) {
  return (
    <div className="overflow-x-auto rounded-md border border-gray-200">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
          <tr>
            {head.map((h, i) => (
              <th key={h} className={`px-4 py-3 ${i === head.length - 1 ? "text-right" : "text-left"}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">{children}</tbody>
      </table>
    </div>
  );
}

function Empty() {
  return <div className="text-sm text-gray-500 p-6 text-center">No records found.</div>;
}
